import Grid from '../../Geodata/grid';

// ProjectionConstants
import FVector from '@data/geometry/FVector';
import type { DeepPartial, LineColor, MixerElement, SerializedPipeline } from '@visualizer/types';
import type { ProjectionModeId } from '../constants/Projection';
import { LinesColorModeOptions, selectLinesColorMode } from './ColorScheme';
import { VisualizationElementId } from '@rendering/types/VisualizationElements';

export type LayerManagementActions = {
    /** When you use that the layer will be automatically refreshed. */
    // Switch the active layer and refresh if needed
    setCurrentLayerId: (layerId: string) => void;

    // Create a new layer with default values
    addLayer: (layerId: string) => void;

    // Remove a layer
    deleteLayer: (layerId: string) => void;
    // Merge partial state into the current layer
    updateCurrentLayerState: (layerState: Partial<LayerState>) => void;

    updateLayerState: (id: string, updates: DeepPartial<LayerState>) => void;

    // Read the current layer state
    getActiveLayerState: () => LayerState;

    // Mark a layer as failed during loading/deserialization
    markLayerFailed: (layerId: string, reason?: string) => void;
    clearLayerFailed: (layerId: string) => void;
    isLayerFailed: (layerId: string) => boolean;
};

export type LayerActions = {
    //Wrapper specifically for the Projection panel
    setProjectionPanel: (updates: Partial<ProjectionLayerState>) => void;

    //Wrapper specifically for the Element panel
    setElementPanel: (updates: Partial<ElementLayerState>) => void;

    // Wrapper specifically for the Segments panel
    setSegmentsPanel: (updates: Partial<SegmentsLayerState>) => void;

    // Wrapper specifically for the SimulationItems
    setSimulationItems: (updates: Partial<SimulationItemsLayerState>) => void;

    // Wrapper specifically for the Data
    setDataLayer: (updates: Partial<DataLayerState>) => void;

    // Wrapper specifically for the Performance Metrics panel
    setPerformanceMetricsPanel: (updates: Partial<PerformanceMetricsLayerState>) => void;

    // Reset handler
    resetFilter: () => void;
};

export type FiltersState = {
    threshold: number[];
    indexFilter: boolean;
    floorFilter: boolean;
    closeToTerrain: number[];
    sliceX: number[];
    sliceY: number[];
    sliceZ: number[];

    unobstructed: boolean;
    unobstructedScaled: boolean;
    unobstructedModeOn: number;
    unobstructedModes: {
        VOXEL_CENTER: number;
        VOXEL_CORNERS: number;
    };

    filter_by_polygon: boolean;
    polygonPositions: FVector[];

    only_in_buildable: boolean;
    alone: boolean;
    no_base: boolean;
    index_tolerance: number;
    index_subsort_mode: 0 | 1 | 2 | 3 | 4; // 0 = None, 1= rand, 2 = low-0, 3= low-1, 4 = bary
    closeToBuilding: boolean;
    closeToBuilding_dist: number[];
};

// ProjectionState
export type ProjectionLayerState = {
    // Use the literal union
    SEL_PROJECTION: ProjectionModeId;

    // Projection angle, in the legacy mode this was displayed in degrees
    THETA: number;
};

// Default projection state
export const DEFAULT_PROJECTION: ProjectionLayerState = {
    SEL_PROJECTION: 0,
    THETA: 0,
};

// ElementState
export type SensorColorMode = 0 | 1 | 2;

export type ElementLayerState = {
    normalized: boolean;
    ELEMENT_INVERTED: boolean;
    ELEMENT_SHOW_SELECTED: boolean;
    SHOW_ZERO_VALUE_POINTS: boolean;
    SENSOR_COLOR_MODE: SensorColorMode;
    scaleFactor: number;
    TRANSPARENCY: number;
    SEL_VIS_ELEMENT: VisualizationElementId;
    ELEMENT_SPIN: 0 | 1;
    ELEMENT_CURRENT_DENSITY: 0 | 1 | 2;
    ELEMENT_1TO3: boolean;
    ELEMENT_1EV3: boolean;
    NO_STROKES: boolean;
};

export const DEFAULT_ELEMENT: ElementLayerState = {
    normalized: true,
    ELEMENT_INVERTED: false,
    ELEMENT_SHOW_SELECTED: true,
    SHOW_ZERO_VALUE_POINTS: true,
    SENSOR_COLOR_MODE: 0,
    scaleFactor: 1.0,
    TRANSPARENCY: 0.0,
    SEL_VIS_ELEMENT: VisualizationElementId.Quad,
    ELEMENT_SPIN: 1,
    ELEMENT_CURRENT_DENSITY: 0,
    ELEMENT_1TO3: false,
    ELEMENT_1EV3: false,
    NO_STROKES: false,
};

export const resolveSensorColorMode = (element?: Partial<ElementLayerState> | null): SensorColorMode => {
    const mode = element?.SENSOR_COLOR_MODE;
    if (mode === 0 || mode === 1 || mode === 2) return mode;
    const legacyGrey = (element as any)?.SENSOR_GREYSCALE;
    return legacyGrey ? 1 : 0;
};

export type SimulationItemsLayerState = {
    x: number;
    y: number;
    z: number;
    showRays: boolean;
    raysQuantity: number;
    raysEveryN: number;
    linesLength: number;
    freeTargetLinesLength: number;
    showFreeLines: boolean;
    showIntersectingLines: boolean;
    heatmapX: number;
    heatmapY: number;
    linesColorModeOption: LinesColorModeOptions;
    linesColorMode: LineColor;
    showInts: boolean;
    closestInts: boolean;
    otherInts: boolean;
    whichIntersections: number;
    heatmapModeFv: boolean;
};

// Segments
export type SegmentsLayerState = {
    frameMax: number;
    frameSelected: number;
    frameNames: string[];

    isLocal: boolean;
    isGo?: boolean;
    isAggregate: boolean;

    framesList: number[];
    aggregateRange: [number, number];
    aggregateWrap: boolean;
};

// Added to save the state of the switch Time/Energy
export type DataLayerState = {
    activeResultId: number;
};

export type PerformanceMetricsLayerState = {
    thresholdLevel: number | [number, number] | null;
};

export type PipelineGUIBlockParams = {
    insidePolygonMode?: 'custom' | 'buildable';
};

export type PipelineGUIParams = Record<string, PipelineGUIBlockParams>;

export type ResultDomainOverrides = {
    scalar: [number, number];
    vectorial: [number, number];
};

export type LayerState = {
    geodataId: string;
    pipeline: SerializedPipeline;
    pipelineGUIParams: PipelineGUIParams;
    VECTORIAL_SCALAR_MODE: 0 | 1;
    mixerElements: MixerElement[];
    filter: FiltersState;
    projection: ProjectionLayerState;
    element: ElementLayerState;
    simulationItems: SimulationItemsLayerState;
    segments: SegmentsLayerState;
    data: DataLayerState;
    performanceMetrics: PerformanceMetricsLayerState;
    resultDomainOverrides: ResultDomainOverrides | null;
};

const resolveDefaultSimulationCoordinates = (): Pick<SimulationItemsLayerState, 'x' | 'y' | 'z'> => {
    const hasValidGridDimensions = Grid.gridX > 0 && Grid.gridY > 0 && Grid.gridZ > 0;
    if (!hasValidGridDimensions || !Number.isFinite(Grid.firstActiveID)) {
        return { x: 0, y: 0, z: 0 };
    }

    const [x, y, z] = Grid.indexToXYZ(Grid.firstActiveID);
    if (![x, y, z].every((value) => Number.isFinite(value))) {
        return { x: 0, y: 0, z: 0 };
    }

    return { x, y, z };
};

export const createNewLayerState = (geodataId = ''): LayerState => {
    const defaultSimulationCoordinates = resolveDefaultSimulationCoordinates();

    return {
        geodataId,
        pipeline: {
            blocks: [],
            executionCount: 0,
        },
        pipelineGUIParams: {},
        VECTORIAL_SCALAR_MODE: 0,
        mixerElements: [],
        filter: {
            threshold: [0, 100],
            indexFilter: false,
            floorFilter: false,
            closeToTerrain: [0, Grid.gridZ * Grid.gridRes],
            sliceX: [0, Grid.gridX],
            sliceY: [0, Grid.gridY],
            sliceZ: [0, Grid.gridZ],

            unobstructed: false,
            unobstructedScaled: false,
            unobstructedModeOn: 1,
            unobstructedModes: {
                VOXEL_CENTER: 0,
                VOXEL_CORNERS: 1,
            },

            filter_by_polygon: false,
            polygonPositions: [],

            only_in_buildable: false,
            alone: false,
            no_base: false,
            index_tolerance: 0,
            index_subsort_mode: 0,
            closeToBuilding: false,
            closeToBuilding_dist: [0, Grid.gridRes],
        },
        projection: { ...DEFAULT_PROJECTION },
        element: {
            ...DEFAULT_ELEMENT,
        },
        simulationItems: {
            linesLength: 1,
            freeTargetLinesLength: 100,

            // Show rays
            showRays: false,
            showFreeLines: true,
            showIntersectingLines: false,

            raysQuantity: 2,
            raysEveryN: 2,

            // Show intersections
            showInts: false,
            closestInts: true,
            otherInts: true,

            // Intersections
            whichIntersections: 0,

            // Sensor point selection
            x: defaultSimulationCoordinates.x,
            y: defaultSimulationCoordinates.y,
            z: defaultSimulationCoordinates.z,

            // Heatmap
            heatmapModeFv: false,
            heatmapX: 0,
            heatmapY: 0,

            // Rays color
            linesColorModeOption: LinesColorModeOptions.MultiGradient,
            linesColorMode: selectLinesColorMode(LinesColorModeOptions.MultiGradient),
        },
        segments: {
            frameMax: 0,
            frameSelected: 0,
            frameNames: [],

            isLocal: false,
            isGo: false,
            isAggregate: false,
            framesList: [0],
            aggregateRange: [0, 0],
            aggregateWrap: false,
        },
        data: {
            activeResultId: 0,
        },
        performanceMetrics: {
            thresholdLevel: null,
        },
        resultDomainOverrides: null,
    };
};

const normalizeSimulationItemsState = (
    simulationItems: Partial<SimulationItemsLayerState> | undefined,
    defaults: SimulationItemsLayerState
): SimulationItemsLayerState => {
    const merged = simulationItems ? { ...defaults, ...simulationItems } : defaults;
    const resolvedLinesColorModeOption = Object.values(LinesColorModeOptions).includes(merged.linesColorModeOption as LinesColorModeOptions)
        ? (merged.linesColorModeOption as LinesColorModeOptions)
        : defaults.linesColorModeOption;

    return {
        ...merged,
        linesColorModeOption: resolvedLinesColorModeOption,
        linesColorMode: selectLinesColorMode(resolvedLinesColorModeOption),
        x: typeof merged.x === 'number' && Number.isFinite(merged.x) ? merged.x : defaults.x,
        y: typeof merged.y === 'number' && Number.isFinite(merged.y) ? merged.y : defaults.y,
        z: typeof merged.z === 'number' && Number.isFinite(merged.z) ? merged.z : defaults.z,
    };
};

export const normalizeLayerState = (layer?: Partial<LayerState> | null, geodataId = layer?.geodataId ?? ''): LayerState => {
    const defaults = createNewLayerState(geodataId);
    const element = layer?.element
        ? {
              ...defaults.element,
              ...layer.element,
              SENSOR_COLOR_MODE: resolveSensorColorMode(layer.element),
          }
        : defaults.element;

    return {
        ...defaults,
        ...layer,
        geodataId: layer?.geodataId ?? geodataId,
        pipeline: layer?.pipeline ? { ...defaults.pipeline, ...layer.pipeline } : defaults.pipeline,
        pipelineGUIParams: layer?.pipelineGUIParams ?? defaults.pipelineGUIParams,
        mixerElements: layer?.mixerElements ?? defaults.mixerElements,
        filter: layer?.filter
            ? {
                  ...defaults.filter,
                  ...layer.filter,
                  unobstructedModes: {
                      ...defaults.filter.unobstructedModes,
                      ...layer.filter.unobstructedModes,
                  },
              }
            : defaults.filter,
        projection: layer?.projection ? { ...defaults.projection, ...layer.projection } : defaults.projection,
        element,
        simulationItems: normalizeSimulationItemsState(layer?.simulationItems, defaults.simulationItems),
        segments: layer?.segments ? { ...defaults.segments, ...layer.segments } : defaults.segments,
        data: layer?.data ? { ...defaults.data, ...layer.data } : defaults.data,
        performanceMetrics: layer?.performanceMetrics
            ? { ...defaults.performanceMetrics, ...layer.performanceMetrics }
            : defaults.performanceMetrics,
        resultDomainOverrides: layer?.resultDomainOverrides ?? defaults.resultDomainOverrides,
    };
};
